/**
 * DirectInstallButton — single-tap CTA to install the PWA on the user's
 * device. Listens for the `beforeinstallprompt` event (fired by Chrome,
 * Edge, Samsung Internet) and triggers the native prompt. On iOS Safari
 * (which doesn't fire that event) we surface a small dialog with the
 * standard "tap Share → Add to Home Screen" instructions.
 *
 * Renders nothing when the user is already in standalone (installed) mode,
 * so blog posts and the landing page don't show a redundant install button.
 */
import React, { useEffect, useState } from 'react';
import { Download, Smartphone, X } from 'lucide-react';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export default function DirectInstallButton({
  variant = 'primary',
  label = 'Install App — Direct',
  labelEs = 'Instalar App — Directo',
  lang = 'en',
}) {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferred(null);
      return;
    }
    if (isIOS()) {
      setShowIosHint(true);
      return;
    }
    // Fallback: open install instructions inline modal anyway.
    setShowIosHint(true);
  };

  const cta = lang === 'es' ? labelEs : label;
  const isPrimary = variant === 'primary';

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="direct-install-btn"
        className={
          isPrimary
            ? 'inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold text-navy font-h uppercase tracking-wider text-sm font-bold hover:bg-gold-light active:scale-95 shadow-2xl'
            : 'inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-gold text-gold hover:bg-gold/10 font-h uppercase tracking-wider text-xs font-semibold'
        }
      >
        <Download className="w-4 h-4" /> {cta}
      </button>

      {showIosHint && (
        <>
          <div className="fixed inset-0 z-[95] bg-black/70" onClick={() => setShowIosHint(false)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[96] bg-navy-deep border-2 border-gold/60 rounded-xl shadow-2xl p-5 w-[92%] max-w-sm" data-testid="direct-install-ios">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gold/20">
              <div className="flex items-center gap-2 text-gold font-h uppercase tracking-wider text-sm">
                <Smartphone className="w-5 h-5" />
                {lang === 'es' ? 'Instalar en tu dispositivo' : 'Install on your device'}
              </div>
              <button onClick={() => setShowIosHint(false)} className="text-gold/70 hover:text-gold" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {lang === 'es' ? (
              <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-200">
                <li>Tocá el botón <strong>Compartir</strong> (cuadrado con flecha) en la parte inferior de Safari.</li>
                <li>Buscá la opción <strong>"Agregar a Pantalla de Inicio"</strong>.</li>
                <li>Tocá <strong>Agregar</strong>. La app se instala como ícono nativo.</li>
              </ol>
            ) : (
              <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-200">
                <li>Tap the <strong>Share</strong> button (square with up arrow) at the bottom of your browser.</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
                <li>Tap <strong>Add</strong>. The app installs as a native icon.</li>
              </ol>
            )}
            <p className="mt-3 text-xs text-gray-400">
              {lang === 'es'
                ? 'Funciona offline. Mismo rendimiento que una app nativa.'
                : 'Works offline. Same performance as a native app.'}
            </p>
          </div>
        </>
      )}
    </>
  );
}
